import React from "react";
import { Box, Stack } from "@mui/material";
import { useApp, useTheme } from "contexts";
import { Custom, Item } from "components";
import { Activities } from "assets/data";
import { ActivityType } from "types";

type Props = {};

export const Activity = (props: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();

  const groupByYear = (data: ActivityType[]) => {
    const grouped: Record<string, ActivityType[]> = {};

    data.forEach((item) => {
      const startYear = new Date(item.from).getFullYear();
      const endYear = item.to
        ? new Date(item.to).getFullYear()
        : new Date().getFullYear();
      const yearKey =
        startYear === endYear ? `${startYear}` : `${startYear}-${endYear}`;

      if (!grouped[yearKey]) {
        grouped[yearKey] = [];
      }
      grouped[yearKey].push(item);
    });

    const sorted = Object.keys(grouped);

    sorted.sort((a, b) => {
      const parseKey = (key: string) => {
        const parts = key.split("-").map(Number);
        return parts.length === 1 ? [parts[0], parts[0]] : parts;
      };

      const [aStart, aEnd] = parseKey(a);
      const [bStart, bEnd] = parseKey(b);

      if (aEnd !== bEnd) return bEnd - aEnd;
      else return bStart - aStart;
    });

    return { sorted, grouped };
  };

  const { sorted, grouped } = groupByYear(Activities);

  const renderYearGroup = (key?: string, children?: React.ReactNode) => (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing.sm,
          mb: theme.spacing.md,
        }}
      >
        {/* Retro Marker Box */}
        <Box
          sx={{
            width: 14,
            height: 14,
            backgroundColor: theme.color.background.color,
            border: `2px solid ${theme.color.primary.color}`,
            borderRadius: theme.border.square,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              backgroundColor: theme.color.primary.color,
            }}
          />
        </Box>

        <Stack direction="row" alignItems="center" spacing={theme.spacing.xs}>
          <Box
            component="span"
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: theme.font.xxs,
              fontWeight: theme.font.bold,
              color: key ? theme.color.primary.color : theme.color.font.accent,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {key
              ? `${t.message.history_step} ${key.replace("-", " — ")}`
              : t.message.history_bottom}
          </Box>
        </Stack>
      </Box>
      <Box sx={{ pl: { xs: theme.spacing.md, sm: theme.spacing.lg, md: theme.spacing.xl } }}>
        {children}
      </Box>
    </Box>
  );

  return (
    <Box
      component="section"
      id="history"
      sx={{ pt: { xs: theme.spacing.xl, md: theme.spacing.xxl }, pb: theme.spacing.sm, width: "100%", position: "relative" }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "flex-end" },
          justifyContent: "space-between",
          mb: theme.spacing.xl,
          gap: theme.spacing.sm,
        }}
      >
        <Box>
          <Box
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: theme.font.xxs,
              fontWeight: theme.font.bold,
              color: theme.color.primary.color,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            {t.sections.history_badge}
          </Box>
          <Custom.Typography
            variant="h2"
            sx={{
              fontSize: { xs: theme.font.xl, md: theme.font.xxl },
              fontWeight: theme.font.extraBold,
              letterSpacing: "-0.02em",
              mt: theme.spacing.xxs,
            }}
          >
            {t.title.history}
          </Custom.Typography>
        </Box>
        <Box
          sx={{
            fontFamily: '"Space Mono", monospace',
            fontSize: theme.font.xxs,
            color: theme.color.font.accent,
          }}
        >
          {t.sections.history_range}
        </Box>
      </Box>

      {/* Timeline Stepped Tree */}
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 8,
              bottom: 24,
              left: 6,
              width: 2,
              borderLeft: `2px dashed ${theme.color.border.color}`,
            },
          }}
        >
          {sorted.map((key: string) => (
            <Box key={key}>
              {renderYearGroup(
                key,
                grouped[key].map((activity: ActivityType, i: number) => (
                  <Item.Activity
                    key={i}
                    item={activity}
                    head={i === 0}
                    tail={i === grouped[key].length - 1}
                  />
                ))
              )}
            </Box>
          ))}
          {renderYearGroup()}
        </Box>
      </Box>
    </Box>
  );
};